import type { FormEvent, ReactNode } from 'react'

export const INQUIRY_KIND = {
  vendorApplication: 'vendor_application',
  b2bEnquiry: 'b2b_enquiry',
} as const

export type InquiryKind = (typeof INQUIRY_KIND)[keyof typeof INQUIRY_KIND]

export const INQUIRY_COUNTRY = 'India'

export const INQUIRY_COUNTRIES = [{ value: INQUIRY_COUNTRY, label: 'India' }] as const

export const INDIA_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const

export const INDIA_STATE_OPTIONS = INDIA_STATES.map((state) => ({
  value: state,
  label: state,
}))

export const B2B_REQUIREMENT = {
  corporateGifting: 'corporate_gifting',
  flowerBouquets: 'flower_bouquets',
  plantsRental: 'plants_rental',
  employeeKits: 'employee_kits',
  festiveDecor: 'festive_decor',
  cakesChocolates: 'cakes_chocolates',
  stationery: 'stationery',
  other: 'other',
} as const

export type B2bRequirementKey =
  (typeof B2B_REQUIREMENT)[keyof typeof B2B_REQUIREMENT]

export const B2B_REQUIREMENT_OPTIONS: {
  id: B2bRequirementKey
  label: string
}[] = [
  { id: B2B_REQUIREMENT.corporateGifting, label: 'Corporate Gifting' },
  { id: B2B_REQUIREMENT.flowerBouquets, label: 'Flower Bouquets & Arrangements' },
  { id: B2B_REQUIREMENT.plantsRental, label: 'Plants / Plant Rental' },
  { id: B2B_REQUIREMENT.employeeKits, label: 'Employee Kits & Celebrations' },
  { id: B2B_REQUIREMENT.festiveDecor, label: 'Festive & Office Décor' },
  { id: B2B_REQUIREMENT.cakesChocolates, label: 'Cakes, Chocolates & Gifts' },
  { id: B2B_REQUIREMENT.stationery, label: 'Stationery & Office Supplies' },
  { id: B2B_REQUIREMENT.other, label: 'Other' },
]

export type VendorRegistrationForm = {
  username: string
  email: string
  firstName: string
  lastName: string
  storeName: string
  address1: string
  address2: string
  country: string
  city: string
  state: string
  postcode: string
  storePhone: string
}

export const EMPTY_VENDOR_REGISTRATION: VendorRegistrationForm = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  storeName: '',
  address1: '',
  address2: '',
  country: INQUIRY_COUNTRY,
  city: '',
  state: '',
  postcode: '',
  storePhone: '',
}

export type CorporateEnquiryForm = {
  name: string
  company: string
  email: string
  phone: string
  requirements: B2bRequirementKey[]
  description: string
  deliveryCity: string
  preferredDate: string
  quantity: string
  budget: string
  gstInvoice: boolean
}

export const EMPTY_CORPORATE_ENQUIRY: CorporateEnquiryForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  requirements: [],
  description: '',
  deliveryCity: '',
  preferredDate: '',
  quantity: '',
  budget: '',
  gstInvoice: false,
}

export type VendorInquiryPayload = {
  username: string
  email: string
  firstName: string
  lastName: string
  storeName: string
  address1: string
  address2: string | null
  country: string
  city: string | null
  state: string
  postcode: string
  storePhone: string
}

export type B2bInquiryPayload = {
  name: string
  company: string
  email: string
  phone: string
  requirements: B2bRequirementKey[]
  description: string
  deliveryCity: string
  preferredDate: string | null
  quantity: string | null
  budget: string | null
  gstInvoice: boolean
}

export type InquiryCreated = {
  id: string
  kind: InquiryKind
  createdAt: string
}

export type InquiryHeroVariant = 'vendor' | 'b2b'

export type InquiryHighlight = {
  id: string
  label: string
}

export type InquiryFormLayoutProps = {
  variant: InquiryHeroVariant
  eyebrow?: string
  title: string
  lead: string
  highlights?: readonly InquiryHighlight[]
  formTitle?: string
  formLead?: string
  children: ReactNode
}

export const VENDOR_REGISTRATION_COPY = {
  eyebrow: 'Join Our',
  title: 'Vendor Collaboration',
  lead: 'Partner with Maaliondoor and grow your business by reaching more customers across multiple locations.',
  highlights: [
    { id: 'business', label: 'More Business' },
    { id: 'reach', label: 'Wider Reach' },
    { id: 'partnership', label: 'Long Term Partnership' },
  ] as const,
  formTitle: 'Vendor Registration',
  formLead: 'Share a few details so we can set up your vendor partnership.',
  sections: {
    details: 'Your Details',
    store: 'Store Details',
    address: 'Store Address',
  },
  note: "We'll review your application and contact you to set up your vendor login. Passwords are not collected here.",
  submit: 'Submit Application',
  success: "Thanks — we've received your application. Our team will get in touch shortly.",
  error: 'Could not submit your application. Please try again.',
  privacy: 'Your information is safe with us. We respect your privacy.',
  fields: {
    username: { label: 'Username', placeholder: 'Choose a username' },
    email: { label: 'Email', placeholder: 'name@business.com' },
    firstName: { label: 'First Name', placeholder: 'First name' },
    lastName: { label: 'Last Name', placeholder: 'Last name' },
    storeName: { label: 'Store / Business Name', placeholder: 'Store or business name' },
    address1: { label: 'Address Line 1', placeholder: 'Street address' },
    address2: { label: 'Address Line 2', placeholder: 'Apartment, suite, landmark (optional)' },
    country: { label: 'Country' },
    city: { label: 'City / Town', placeholder: 'City or town' },
    state: { label: 'State', placeholder: 'Select state' },
    postcode: { label: 'PIN Code', placeholder: '6-digit PIN code' },
    storePhone: { label: 'Store Phone', placeholder: '10-digit mobile number' },
  },
} as const

export const CORPORATE_ENQUIRY_COPY = {
  brand: 'maaliondoor',
  tagline: 'Flowers, Plants & Gifting...',
  title: 'B2B & Corporate Enquiry',
  lead: 'Tell us what you need and our team will get back to you.',
  formTitle: 'Your Details',
  formLead: 'Share a few details so we can prepare the right proposal.',
  sections: {
    details: 'Your Details',
    requirement: 'Your Requirement',
    delivery: 'Delivery & Budget',
  },
  fields: {
    name: { label: 'Name', placeholder: 'Your name' },
    company: { label: 'Company / Organisation', placeholder: 'Company name' },
    email: { label: 'Business Email', placeholder: 'name@company.com' },
    phone: { label: 'Mobile Number', placeholder: '10-digit mobile number' },
    description: {
      label: 'Briefly describe your requirement',
      placeholder: 'Occasions, quantity, styling notes, delivery windows…',
    },
    deliveryCity: { label: 'Delivery City / Location', placeholder: 'City or area' },
    preferredDate: { label: 'Preferred Delivery Date' },
    quantity: { label: 'Approx. Quantity', placeholder: 'e.g. 50 hampers' },
    budget: { label: 'Approx. Budget', placeholder: 'e.g. ₹50,000' },
    gstInvoice: 'GST Invoice Required',
  },
  submit: 'Submit Enquiry',
  success: "Thanks — we've received your enquiry. Our team will get back to you shortly.",
  error: 'Could not submit your enquiry. Please try again.',
  privacy: 'Your information is safe with us. We respect your privacy.',
} as const

export const INQUIRY_VALIDATION = {
  required: 'Please fill all required fields.',
  email: 'Enter a valid email address.',
  phone: 'Enter a valid 10-digit mobile number.',
  requirements: 'Select at least one requirement.',
  description: 'Please describe your requirement in a little more detail.',
} as const

export type InquiryFieldProps = {
  id: string
  label: string
  required?: boolean
}

export type InquiryTextFieldProps = InquiryFieldProps & {
  type?: 'text' | 'email' | 'tel' | 'date'
  placeholder?: string
  value: string
  autoComplete?: string
  disabled?: boolean
  onChange: (value: string) => void
}

export type InquirySelectFieldProps = InquiryFieldProps & {
  value: string
  placeholder?: string
  options: readonly { value: string; label: string }[]
  disabled?: boolean
  onChange: (value: string) => void
}

export type InquiryHeroArtProps = {
  variant: InquiryHeroVariant
}

export type UseInquiryFormResult<T> = {
  form: T
  update: (patch: Partial<T>) => void
  submitting: boolean
  submitted: boolean
  error: string | null
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}
